import { useEffect, useId, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { WorkspaceDialog } from './WorkspaceDialog';
import { hostWords } from '../shared/host-words';

export function RenameJobDialog({
  name,
  otherNames,
  onRename,
  onClose,
}: {
  name: string;
  otherNames: string[];
  onRename: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const inFlight = useRef(false);
  const errorId = useId();
  const trimmed = draft.trim();
  const duplicate = otherNames.some(
    (value) => value.trim().toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
  );
  const invalid = !trimmed || duplicate || trimmed.length > 160;
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  const close = () => {
    if (!inFlight.current) onClose();
  };
  const save = async () => {
    if (invalid || inFlight.current || trimmed === name) return;
    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      await onRename(trimmed);
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'The job could not be renamed. Try again.',
      );
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <WorkspaceDialog
      telemetry="rename-job"
      title={`Rename ${hostWords().job}`}
      compact
      onClose={close}
    >
      <form
        className="rename-job-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        aria-busy={saving}
      >
        <label>
          <span>Job name</span>
          <input
            ref={input}
            value={draft}
            maxLength={160}
            disabled={saving}
            onChange={(event) => {
              setDraft(event.target.value);
              setError('');
            }}
            aria-invalid={duplicate || Boolean(error)}
            aria-describedby={duplicate || error ? errorId : undefined}
          />
        </label>
        {(duplicate || error) && (
          <p
            id={errorId}
            role="alert"
            data-telemetry={duplicate ? 'error:job-name-taken' : 'error:job-rename-failed'}
          >
            {duplicate ? 'A job with this name already exists. Choose a different name.' : error}
          </p>
        )}
        <footer>
          <button type="button" className="secondary-button" disabled={saving} onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={invalid || saving || trimmed === name}
          >
            {saving && <LoaderCircle className="spin" size={15} />}
            {saving ? 'Saving…' : 'Save name'}
          </button>
        </footer>
      </form>
    </WorkspaceDialog>
  );
}
